import { useTranslation } from 'react-i18next'

import { HStack, TertiaryButton } from '@enechain/ecloud-designsystem'

import {
  DeleteIconButton,
  EditIconButton,
} from '~/components/common/MasterDataTable/RowActionButtons'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import { isIntradayDraftEditable } from '~/pages/IntradayBidding/models/intradayGateClose'

/** 行の操作列に出すボタン。 */
export type IntradayBidRowActionFlags = {
  /** 編集ボタン (詳細モーダルを編集できる形で開く)。 */
  edit: boolean
  /** 取消ボタン (板取消 AC-BIN-008 / 予約取消 AC-BIN-093 の確認モーダルを開く)。 */
  withdraw: boolean
  /** 下書き削除ボタン。 */
  deleteDraft: boolean
  /** 下書きに戻すボタン (送信中の札、 AC-BIN-115)。 */
  returnToDraft: boolean
}

/**
 * 行の操作列に出すボタン (#10110)。
 *
 * - 編集: 詳細モーダルが編集できる形で開く札 = 下書きで GC 前 ([[isIntradayDraftEditable]]、
 *   AC-BIN-007)。 それ以外の札は行を開いても表示専用なので、 編集ボタンは置かない
 * - 取消: backend が取消できると返した札 (`withdrawable`、 状態と GC から判定)
 * - 下書きに戻す: backend が下書きに戻せると返した札 (`returnableToDraft`、 送信中で GC 前・
 *   確定済ワークフロー経由でない)。 送信中の札に取消は出ない (AC-BIN-008)
 *
 * `now` は呼び出し側が分単位で引き直した時刻を渡す (GC を跨いだら編集ボタンを消す)。
 */
export const intradayBidRowActionFlags = (
  row: IntradayBidRow,
  now: Date,
): IntradayBidRowActionFlags => ({
  edit: isIntradayDraftEditable(row.approvalState, row.gcAt, now),
  withdraw: row.withdrawable,
  deleteDraft: isIntradayDraftEditable(row.approvalState, row.gcAt, now),
  returnToDraft: row.returnableToDraft,
})

type Props = {
  row: IntradayBidRow
  flags: IntradayBidRowActionFlags
  /** 完了・削除済みワークフロー経由で開いた場合 true。 ボタンを disabled にする。 */
  isReadonly: boolean
  onEdit: (row: IntradayBidRow) => void
  onWithdraw: (row: IntradayBidRow) => void
  onDelete?: (row: IntradayBidRow) => void
  /** 「下書きに戻す」 の callback。 未指定なら flags に関わらず描画しない。 */
  onReturnToDraft?: (row: IntradayBidRow) => void
  /** 下書きに戻す遷移の最中か。 ボタンを disabled にして連打を止める。 */
  isReturningToDraft?: boolean
}

/**
 * 時間前の札の行の編集・取消ボタン。 入札一覧 (`IntradayBidTable`、 業務ワークフロー内も同じ) と
 * 板情報の自社入札 (`MarketBoardTab`) で同じ出し方にする (#10110、 design `intraday.html`
 * `td.im-row-actions` / `intraday-price.html` `td.im-mybids-actions`)。
 *
 * 編集 = pen-to-square (tertiary)、 取消 = ゴミ箱 (outline-danger) のアイコンボタンを
 * 編集・取消の順に右揃えで並べ、 ボタン名は hover と aria-label に出す。 置くボタンが
 * 無い行の「—」 は呼び出し側が出す (入札一覧は承認・差戻しも同じ列に並ぶため)。
 */
export const IntradayBidRowActions = ({
  row,
  flags,
  isReadonly,
  onEdit,
  onWithdraw,
  onDelete,
  onReturnToDraft,
  isReturningToDraft = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const canDelete = flags.deleteDraft && onDelete !== undefined
  // AC-BIN-050: 確定済ワークフロー経由の札の下書き削除は backend が拒否する (#12594 決定 1)。
  // 画面単位の読み取り専用 (AC-WFW-014) の時はそちらの扱い (理由を出さない従来の非活性) を優先する。
  const historyLockReason =
    canDelete && row.fromCompletedWorkflow === true && !isReadonly
      ? t('page:biddingIntraday.workflowLock.fromCompletedWorkflow')
      : undefined
  return (
    <HStack justifyContent="flex-end" gap={2}>
      {flags.edit && (
        <EditIconButton
          ariaLabel={t('page:biddingIntraday.action.editThisBid')}
          disabled={isReadonly}
          onClick={(e) => {
            // 行クリック (= 同じ詳細モーダル) に伝播させて二重に開かない。
            e.stopPropagation()
            onEdit(row)
          }}
        />
      )}
      {(canDelete || flags.withdraw) && (
        <DeleteIconButton
          ariaLabel={t(
            canDelete
              ? 'page:biddingIntraday.action.deleteThisBid'
              : 'page:biddingIntraday.action.withdrawThisBid',
          )}
          disabled={isReadonly || historyLockReason !== undefined}
          disabledReason={historyLockReason}
          onClick={() => (canDelete ? onDelete(row) : onWithdraw(row))}
        />
      )}
      {/* design intraday.html: 送信中の行は文字の tertiary ボタン。 確認モーダルは出さない */}
      {flags.returnToDraft && onReturnToDraft !== undefined && (
        <TertiaryButton
          disabled={isReadonly || isReturningToDraft}
          onClick={() => onReturnToDraft(row)}
          size="md"
        >
          {t('page:biddingIntraday.action.returnToDraft')}
        </TertiaryButton>
      )}
    </HStack>
  )
}
